import { useEffect, useState, type FC } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { routes } from '@/router/routes'
import { useAppStore } from '@/store'
import { Modal, Button } from '@/components'
import { useOnlineStatus } from '@/helpers/hooks'
import { getMs } from '@/helpers'
import { E_ShareholderStatuses } from '@/enums'
import { useOrganizationsDrawerStore } from '@/pages/home/components/certificates/organizations-drawer/store'
import { useHelpStore } from '@/pages/help/store'
import { rqKeys } from '@/react-query-keys'
import { apiRequest } from '@/helpers/request'
import { profile } from '@/profile'
import type { T_UniRes } from '@/types'
import type { T_TicketsListRes } from '@/types/responses'
import iconHomeSvg from './img/icon-home.svg'
import iconHomeActiveSvg from './img/icon-home-active.svg'
import iconRetailSvg from './img/icon-retail.svg'
import iconRetailActiveSvg from './img/icon-retail-active.svg'
import iconGasStationSvg from './img/icon-gas-station.svg'
import iconGasStationActiveSvg from './img/icon-gas-station-active.svg'
import iconTravelSvg from './img/icon-travel.svg'
import iconTravelActiveSvg from './img/icon-travel-active.svg'
import iconHelpSvg from './img/icon-help.svg'
import iconHelpActiveSvg from './img/icon-help-active.svg'
import {
  SC_BottomBarWrapper,
  SC_AppSectionBtn,
} from './styled'


export const BottomBar: FC = () => {
  const userData = useAppStore(s => s.userData)
  const isOnline = useOnlineStatus()
  const retailLabel = 'Ритейл'

  const isAtLeastOneUnreadMessage = useHelpStore((state) => state.isAtLeastOneUnreadMessage)
  const setIsAtLeastOneUnreadMessage = useHelpStore((state) => state.setIsAtLeastOneUnreadMessage)

  const isAuthorized = useAppStore((s) => s.isAuthorized)

  const isShareholder = (
    userData?.shareholderStatusId !== E_ShareholderStatuses.None
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Want
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Waiting
  ) || (userData?.sourceCompanyId !== 0 && profile.functionalAll)

  const showOrganizationsDrawer = useOrganizationsDrawerStore((s) => s.showOrganizationsDrawer)

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const handleOk = () => setIsModalOpen(false)

  const guardShareholder = (action: () => void) => () => {
    if (isShareholder) action()
    else setIsModalOpen(true)
  }

  const guardOnline = (action: () => void) => () => {
    if (isOnline) {
      action()
    } else {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
    }
  }

  const isPathsActive = (paths: string | string[]) => (
    (
      Array.isArray(paths)
        ? paths
        : [ paths ]
    ).includes(pathname)
  )

  const isGasStationPathActive = (
    isPathsActive([
      routes.gasStation,
      routes.gasStationFueling,
      routes.gasStationFuelingComplete,
    ])
    || pathname.startsWith(`${routes.gasStationFueling}/`)
  )

  const { data: ticketsList } = useQuery<T_UniRes<T_TicketsListRes>>({
    queryKey: [ rqKeys.ticketsList ],
    queryFn: () => apiRequest({ method: 'ticket/query/list' }),
    enabled: isAuthorized,
    // Важно: глобально staleTime=0 + refetchOnWindowFocus=true ⇒ может казаться "постоянным запросом".
    // Для тикетов в UI нам достаточно обновления по backend notifications + ручных переходов.
    staleTime: getMs('5m'),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  useEffect(() => {
    const list = ticketsList?.data?.list || []

    const hasUnread = !!list
      .filter((r) => r.status.title !== 'Закрыт')
      .filter((r) => r.unread)
      .length

    setIsAtLeastOneUnreadMessage(hasUnread)
  }, [ ticketsList ])


  if (!isAuthorized) return null


  return <>
    <SC_BottomBarWrapper>
      {/* Home */}
      <SC_AppSectionBtn onClick={() => navigate(routes.home)}>
        <img src={isPathsActive(routes.home) ? iconHomeActiveSvg : iconHomeSvg} />
        <span>Домой</span>
      </SC_AppSectionBtn>

      {/* Retail */}
      <SC_AppSectionBtn
        onClick={
          guardOnline(
            guardShareholder(() => {
              if (pathname !== routes.home) navigate(routes.home)
              showOrganizationsDrawer()
            })
          )
        }
      >
        <img src={isPathsActive(routes.retail) ? iconRetailActiveSvg : iconRetailSvg} />
        <span>{retailLabel}</span>
      </SC_AppSectionBtn>

      {/* Gas stations */}
      <SC_AppSectionBtn
        onClick={
          guardOnline(
            guardShareholder(() => navigate(routes.gasStation))
          )
        }
      >
        <img
          src={
            isGasStationPathActive
              ? iconGasStationActiveSvg
              : iconGasStationSvg
          }
        />

        <span>АЗС</span>
      </SC_AppSectionBtn>

      {/* Travel */}
      <SC_AppSectionBtn
        onClick={
          guardOnline(
            guardShareholder(() => navigate(routes.travel))
          )
        }
      >
        <img src={isPathsActive(routes.travel) ? iconTravelActiveSvg : iconTravelSvg} />
        <span>Путешествия</span>
      </SC_AppSectionBtn>

      {/* Help */}
      <SC_AppSectionBtn
        $hasUnread={isAtLeastOneUnreadMessage}
        onClick={
          guardOnline(() => navigate(routes.help))
        }
      >
        <img src={isPathsActive(routes.help) ? iconHelpActiveSvg : iconHelpSvg} />
        <span>Помощь</span>
      </SC_AppSectionBtn>
    </SC_BottomBarWrapper>

    <Modal
      open={isModalOpen}
      centered
      closable={false}
      footer={
        <Button key='ok' type='primary' block onClick={handleOk}>
          Хорошо
        </Button>
      }
    >
      <h3>Для доступа к полному функционалу нашего приложения станьте пайщиком</h3>
    </Modal>
  </>
}
